import React, { useState, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import {
  makeStyles,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  CircularProgress,
  Box,
  Chip,
  Tooltip,
  Fade,
  useTheme,
  Avatar,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import { green, orange, red } from "@material-ui/core/colors";

// Icons
import {
  Edit,
  CheckCircle,
  SignalCellularConnectedNoInternet2Bar,
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  CropFree,
  DeleteOutline,
  Add as AddIcon,
  WhatsApp as WhatsAppIcon,
  Sync as SyncIcon,
  PowerSettingsNew as PowerIcon,
} from "@material-ui/icons";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === "dark" ? "#0a0a0f" : "#f0f4f8",
    minHeight: "calc(100vh - 70px)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  // Header
  header: {
    marginBottom: theme.spacing(3),
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(3),
    gap: theme.spacing(2),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  titleSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  titleIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    boxShadow: "0 4px 14px rgba(37, 211, 102, 0.4)",
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  subtitle: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  // Add Button
  addButton: {
    background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
    color: "#fff",
    borderRadius: 10,
    padding: theme.spacing(1, 2.5),
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 4px 14px rgba(37, 211, 102, 0.4)",
    "&:hover": {
      background: "linear-gradient(135deg, #20BD5A 0%, #0E7A6B 100%)",
      boxShadow: "0 6px 20px rgba(37, 211, 102, 0.5)",
    },
  },
  // Stats
  statsRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
  },
  statCard: {
    padding: theme.spacing(2, 3),
    borderRadius: 12,
    background: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    minWidth: 180,
  },
  statIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  // Table
  tableContainer: {
    borderRadius: 16,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
  },
  tableWrapper: {
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },
  table: {
    "& .MuiTableHead-root": {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      "& .MuiTableCell-head": {
        fontWeight: 600,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        padding: theme.spacing(2),
        whiteSpace: "nowrap",
      },
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: "all 0.15s ease",
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
      },
      "& .MuiTableCell-body": {
        padding: theme.spacing(1.5, 2),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
      },
    },
  },
  // Connection Row
  connectionInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  connectionAvatar: {
    width: 42,
    height: 42,
    borderRadius: 12,
    background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
  },
  connectionName: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: "0.9rem",
  },
  // Status chips
  statusChip: {
    borderRadius: 8,
    fontWeight: 600,
    fontSize: "0.75rem",
    height: 28,
  },
  statusConnected: {
    backgroundColor: alpha(green[500], 0.15),
    color: green[700],
    "& .MuiChip-icon": {
      color: green[500],
    },
  },
  statusDisconnected: {
    backgroundColor: alpha(red[500], 0.15),
    color: red[700],
    "& .MuiChip-icon": {
      color: red[500],
    },
  },
  statusQrcode: {
    backgroundColor: alpha(orange[500], 0.15),
    color: orange[700],
    "& .MuiChip-icon": {
      color: orange[500],
    },
  },
  statusOpening: {
    backgroundColor: alpha(theme.palette.info.main, 0.15),
    color: theme.palette.info.main,
  },
  // Action Buttons
  actionButtonsWrapper: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "center",
    flexWrap: "wrap",
  },
  sessionBtn: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.75rem",
    padding: theme.spacing(0.5, 1.5),
  },
  qrBtn: {
    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #5558E3 0%, #7C4FE0 100%)",
    },
  },
  disconnectBtn: {
    borderColor: orange[500],
    color: orange[700],
    "&:hover": {
      backgroundColor: alpha(orange[500], 0.1),
    },
  },
  retryBtn: {
    borderColor: green[500],
    color: green[700],
    "&:hover": {
      backgroundColor: alpha(green[500], 0.1),
    },
  },
  actionBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
    },
  },
  editBtn: {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
  },
  deleteBtn: {
    color: theme.palette.error.main,
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    "&:hover": {
      backgroundColor: alpha(theme.palette.error.main, 0.2),
    },
  },
  // Default badge
  defaultBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  defaultIcon: {
    color: green[500],
    fontSize: 24,
  },
  // Empty state
  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
  },
  emptyIcon: {
    width: 80,
    height: 80,
    borderRadius: 20,
    background: alpha("#25D366", 0.1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    marginBottom: theme.spacing(2),
    color: "#25D366",
  },
}));

const Connections = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(confirmationModalInitialState);

  // Stats
  const connectedCount = whatsApps?.filter((w) => w.status === "CONNECTED").length || 0;
  const disconnectedCount = whatsApps?.filter((w) => w.status === "DISCONNECTED").length || 0;
  const totalCount = whatsApps?.length || 0;

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = () => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
  }, []);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, []);

  const handleEditWhatsApp = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId,
      });
    }
    if (action === "delete") {
      setConfirmModalInfo({
        action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }
    setConfirmModalInfo(confirmationModalInitialState);
  };

  const renderStatusChip = (whatsApp) => {
    const statusMap = {
      CONNECTED: {
        label: "Conectado",
        className: classes.statusConnected,
        icon: <SignalCellular4Bar style={{ fontSize: 16 }} />,
      },
      DISCONNECTED: {
        label: "Desconectado",
        className: classes.statusDisconnected,
        icon: <SignalCellularConnectedNoInternet0Bar style={{ fontSize: 16 }} />,
      },
      qrcode: {
        label: "QR Code",
        className: classes.statusQrcode,
        icon: <CropFree style={{ fontSize: 16 }} />,
      },
      OPENING: {
        label: "Conectando...",
        className: classes.statusOpening,
        icon: <CircularProgress size={14} />,
      },
      TIMEOUT: {
        label: "Timeout",
        className: classes.statusDisconnected,
        icon: <SignalCellularConnectedNoInternet2Bar style={{ fontSize: 16 }} />,
      },
      PAIRING: {
        label: "Pareando",
        className: classes.statusQrcode,
        icon: <SyncIcon style={{ fontSize: 16 }} />,
      },
    };

    const status = statusMap[whatsApp.status] || statusMap.DISCONNECTED;

    return (
      <Chip
        icon={status.icon}
        label={status.label}
        size="small"
        className={`${classes.statusChip} ${status.className}`}
      />
    );
  };

  const renderActionButtons = (whatsApp) => {
    return (
      <Box className={classes.actionButtonsWrapper}>
        {(whatsApp.status === "qrcode" || whatsApp.status === "pairingcode") && (
          <Button
            size="small"
            variant="contained"
            className={`${classes.sessionBtn} ${classes.qrBtn}`}
            onClick={() => handleOpenQrModal(whatsApp)}
          >
            {whatsApp.status === "pairingcode" 
              ? "Ver Código" 
              : i18n.t("connections.buttons.qrcode")}
          </Button>
        )}
        {whatsApp.status === "DISCONNECTED" && (
          <>
            <Button
              size="small"
              variant="outlined"
              className={`${classes.sessionBtn} ${classes.retryBtn}`}
              onClick={() => handleStartWhatsAppSession(whatsApp.id)}
            >
              {i18n.t("connections.buttons.tryAgain")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              className={classes.sessionBtn}
              onClick={() => handleRequestNewQrCode(whatsApp.id)}
            >
              {i18n.t("connections.buttons.newQr")}
            </Button>
          </>
        )}
        {(whatsApp.status === "CONNECTED" ||
          whatsApp.status === "PAIRING" ||
          whatsApp.status === "TIMEOUT") && (
          <Button
            size="small"
            variant="outlined"
            className={`${classes.sessionBtn} ${classes.disconnectBtn}`}
            startIcon={<PowerIcon style={{ fontSize: 16 }} />}
            onClick={() => handleOpenConfirmationModal("disconnect", whatsApp.id)}
          >
            {i18n.t("connections.buttons.disconnect")}
          </Button>
        )}
        {whatsApp.status === "OPENING" && (
          <Button size="small" variant="outlined" disabled className={classes.sessionBtn}>
            {i18n.t("connections.buttons.connecting")}
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box className={classes.root}>
      {/* Modals */}
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      <QrcodeModal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
      />
      <WhatsAppModal
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />

      {/* Header */}
      <Fade in timeout={400}>
        <Box className={classes.header}>
          <Box className={classes.headerTop}>
            <Box className={classes.titleSection}>
              <Box className={classes.titleIcon}>
                <WhatsAppIcon />
              </Box>
              <Box>
                <Typography className={classes.title}>
                  {i18n.t("connections.title")}
                </Typography>
                <Typography className={classes.subtitle}>
                  Gerencie suas conexões do WhatsApp
                </Typography>
              </Box>
            </Box>

            <Can
              role={user.profile}
              perform="connections-page:addConnection"
              yes={() => (
                <Button
                  variant="contained"
                  className={classes.addButton}
                  startIcon={<AddIcon />}
                  onClick={handleOpenWhatsAppModal}
                >
                  {i18n.t("connections.buttons.add")}
                </Button>
              )}
            />
          </Box>

          {/* Stats */}
          <Box className={classes.statsRow}>
            <Box className={classes.statCard}>
              <Box
                className={classes.statIcon}
                style={{ background: alpha(green[500], 0.15) }}
              >
                <SignalCellular4Bar style={{ color: green[500] }} />
              </Box>
              <Box>
                <Typography className={classes.statValue}>{connectedCount}</Typography>
                <Typography className={classes.statLabel}>Conectados</Typography>
              </Box>
            </Box>
            <Box className={classes.statCard}>
              <Box
                className={classes.statIcon}
                style={{ background: alpha(red[500], 0.15) }}
              >
                <SignalCellularConnectedNoInternet0Bar style={{ color: red[500] }} />
              </Box>
              <Box>
                <Typography className={classes.statValue}>{disconnectedCount}</Typography>
                <Typography className={classes.statLabel}>Desconectados</Typography>
              </Box>
            </Box>
            <Box className={classes.statCard}>
              <Box
                className={classes.statIcon}
                style={{ background: alpha(theme.palette.primary.main, 0.15) }}
              >
                <WhatsAppIcon style={{ color: theme.palette.primary.main }} />
              </Box>
              <Box>
                <Typography className={classes.statValue}>{totalCount}</Typography>
                <Typography className={classes.statLabel}>Total</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>

      {/* Table */}
      <Fade in timeout={600}>
        <Paper className={classes.tableContainer} elevation={0}>
          <Box className={classes.tableWrapper}>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("connections.table.name")}</TableCell>
                  <TableCell align="center">{i18n.t("connections.table.status")}</TableCell>
                  <Can
                    role={user.profile}
                    perform="connections-page:actionButtons"
                    yes={() => (
                      <TableCell align="center">{i18n.t("connections.table.session")}</TableCell>
                    )}
                  />
                  <TableCell align="center">{i18n.t("connections.table.lastUpdate")}</TableCell>
                  <TableCell align="center">{i18n.t("connections.table.default")}</TableCell>
                  <Can
                    role={user.profile}
                    perform="connections-page:editOrDeleteConnection"
                    yes={() => (
                      <TableCell align="center">{i18n.t("connections.table.actions")}</TableCell>
                    )}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRowSkeleton columns={6} />
                ) : (
                  <>
                    {whatsApps?.length > 0 &&
                      whatsApps.map((whatsApp) => (
                        <TableRow key={whatsApp.id}>
                          <TableCell>
                            <Box className={classes.connectionInfo}>
                              <Avatar className={classes.connectionAvatar}>
                                <WhatsAppIcon />
                              </Avatar>
                              <Typography className={classes.connectionName}>
                                {whatsApp.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">{renderStatusChip(whatsApp)}</TableCell>
                          <Can
                            role={user.profile}
                            perform="connections-page:actionButtons"
                            yes={() => (
                              <TableCell align="center">{renderActionButtons(whatsApp)}</TableCell>
                            )}
                          />
                          <TableCell align="center">
                            <Typography variant="body2" color="textSecondary">
                              {format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {whatsApp.isDefault && (
                              <Box className={classes.defaultBadge}>
                                <CheckCircle className={classes.defaultIcon} />
                              </Box>
                            )}
                          </TableCell>
                          <Can
                            role={user.profile}
                            perform="connections-page:editOrDeleteConnection"
                            yes={() => (
                              <TableCell align="center">
                                <Box display="flex" justifyContent="center" gap={1}>
                                  <Tooltip title="Editar">
                                    <IconButton
                                      size="small"
                                      className={`${classes.actionBtn} ${classes.editBtn}`}
                                      onClick={() => handleEditWhatsApp(whatsApp)}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Excluir">
                                    <IconButton
                                      size="small"
                                      className={`${classes.actionBtn} ${classes.deleteBtn}`}
                                      onClick={() =>
                                        handleOpenConfirmationModal("delete", whatsApp.id)
                                      }
                                    >
                                      <DeleteOutline fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            )}
                          />
                        </TableRow>
                      ))}
                  </>
                )}
              </TableBody>
            </Table>

            {!loading && whatsApps?.length === 0 && (
              <Box className={classes.emptyState}>
                <Box className={classes.emptyIcon}>
                  <WhatsAppIcon style={{ fontSize: 40 }} />
                </Box>
                <Typography variant="h6" gutterBottom>
                  Nenhuma conexão encontrada
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Adicione sua primeira conexão WhatsApp
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};

export default Connections;
